import { createSelector } from '@ngrx/store';
import { DocumentState, featureKey } from '../reducer/documents.reducer';

export const selectAllowedDocumentTypes = (workspaceId: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.allowedDocumentTypes[workspaceId],
  );

export const selectLoadingAllowedDocumentTypes = (workspaceId: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.loadingAllowedDocumentTypes[workspaceId],
  );

export const selectAllowedDocumentTypesError = (workspaceId: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.allowedDocumentTypesError[workspaceId],
  );

export const getDocumentDict = createSelector(
  (state: any) => state[featureKey],
  (documentState: DocumentState) => documentState.documentDict,
);

export const selectRequiredDocumentTypes = (type: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) => documentState.requiredDocumentTypes[type],
  );

export const selectLoadingRequiredDocumentTypes = (type: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.loadingRequiredDocumentTypes[type],
  );

export const selectRequiredDocumentTypesError = (type: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.requiredDocumentTypesError[type],
  );

export const selectDocumentTypes = (type = 'all') =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) => documentState.documentTypes[type],
  );

export const selectLoadingDocumentTypes = (type = 'all') =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) => documentState.loadingDocumentTypes[type],
  );

export const selectDocumentTypesError = (type = 'all') =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) => documentState.documentTypesError[type],
  );

export const selectEntityDocuments = (entityId: string, entityType: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.entityDocuments[entityType]?.[entityId],
  );

export const selectLoadingEntityDocuments = (
  entityId: string,
  entityType: string,
) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.loadingEntityDocuments[entityType]?.[entityId],
  );

export const selectEntityDocumentsError = (
  entityId: string,
  entityType: string,
) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.entityDocumentsError[entityType]?.[entityId],
  );

export const selectAdditionalEntityDocuments = (
  entityId: string,
  entityType: string,
) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.additionalEntityDocuments[entityType]?.[entityId],
  );

export const selectLoadingAdditionalEntityDocuments = (
  entityId: string,
  entityType: string,
) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.loadingAdditionalEntityDocuments[entityType]?.[entityId],
  );

export const selectAdditionalEntityDocumentsError = (
  entityId: string,
  entityType: string,
) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.additionalEntityDocumentsError[entityType]?.[entityId],
  );

export const selectWorkspaceDocuments = (workspaceId: string) =>
  selectEntityDocuments(workspaceId, 'workspace');

export const selectLoadingWorkspaceDocuments = (workspaceId: string) =>
  selectLoadingEntityDocuments(workspaceId, 'workspace');

export const selectWorkspaceDocumentsError = (workspaceId: string) =>
  selectEntityDocumentsError(workspaceId, 'workspace');

export const selectMimeTypes = createSelector(
  (state: any) => state[featureKey],
  (documentState: DocumentState) => documentState.mimeTypes,
);

export const selectLoadingMimeTypes = createSelector(
  (state: any) => state[featureKey],
  (documentState: DocumentState) => documentState.loadingMimeTypes,
);

export const selectMimeTypesError = createSelector(
  (state: any) => state[featureKey],
  (documentState: DocumentState) => documentState.mimeTypesError,
);

export const selectUpdatingDocument = (documentId: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.updatingDocument[documentId],
  );

export const selectUpdatingDocumentError = (documentId: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.updatingDocumentError[documentId],
  );

export const selectDeletingDocument = (documentId: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.deletingDocument[documentId],
  );

export const selectDeletingDocumentError = (documentId: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.deleteDocumentError[documentId],
  );

export const selectUploadingDocument = (entityId: string, uuid: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.uploadingDocument[entityId]?.[uuid] ?? null,
  );

export const selectUploadingEntityDocuments = (entityId: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      Object.values(documentState.uploadingDocument[entityId] ?? {}) ?? [],
  );

export const selectDocumentThumbnails = (documentId: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.documentThumbnails[documentId],
  );

export const selectLoadingDocumentThumbnails = (documentId: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.loadingDocumentThumbnails[documentId],
  );

export const selectDocumentThumbnailError = (documentId: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (documentState: DocumentState) =>
      documentState.documentThumbnailError[documentId],
  );

export const selectActiveDocument = createSelector(
  (state: any) => state[featureKey],
  (documentState: DocumentState) => documentState.activeDocument,
);

export const selectAttributeTypes = createSelector(
  (state: any) => state[featureKey],
  (documentState: DocumentState) => documentState.attributeTypes,
);

export const selectLoadingAttributeTypes = createSelector(
  (state: any) => state[featureKey],
  (documentState: DocumentState) => documentState.loadingAttributeTypes,
);

export const selectAttributeTypesError = createSelector(
  (state: any) => state[featureKey],
  (documentState: DocumentState) => documentState.attributeTypesError,
);
